import { useRef, useEffect, useCallback, useState } from 'react';

export type AnnotationTool = 'pen' | 'arrow' | 'highlight';

export interface AnnotationShape {
  id: string;
  type: AnnotationTool;
  color: string;
  lineWidth: number;
  points: Array<{ x: number; y: number }>; // normalized 0..1 relative to canvas size
}

interface SlideAnnotationCanvasProps {
  shapes: AnnotationShape[];
  onShapesChange: (shapes: AnnotationShape[]) => void;
  tool: AnnotationTool;
  color: string;
  lineWidth: number;
  active: boolean;
  className?: string;
}

// ── Drawing helpers ────────────────────────────────────────────────────────
function denorm(pts: AnnotationShape['points'], w: number, h: number) {
  return pts.map(p => ({ x: p.x * w, y: p.y * h }));
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  pts: AnnotationShape['points'],
  w: number,
  h: number,
  color: string,
  lw: number,
) {
  if (pts.length < 2) return;
  const d = denorm(pts, w, h);
  const from = d[0];
  const to = d[d.length - 1];
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const angle = Math.atan2(dy, dx);
  const headLen = Math.max(14, lw * 5);

  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = lw;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Shaft
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();

  // Arrowhead
  ctx.beginPath();
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(
    to.x - headLen * Math.cos(angle - Math.PI / 6),
    to.y - headLen * Math.sin(angle - Math.PI / 6),
  );
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(
    to.x - headLen * Math.cos(angle + Math.PI / 6),
    to.y - headLen * Math.sin(angle + Math.PI / 6),
  );
  ctx.stroke();
  ctx.restore();
}

function drawHighlight(
  ctx: CanvasRenderingContext2D,
  pts: AnnotationShape['points'],
  w: number,
  h: number,
  color: string,
) {
  if (pts.length < 2) return;
  const d = denorm(pts, w, h);
  const x = Math.min(d[0].x, d[1].x);
  const y = Math.min(d[0].y, d[1].y);
  const rw = Math.abs(d[1].x - d[0].x);
  const rh = Math.abs(d[1].y - d[0].y);

  ctx.save();
  ctx.globalAlpha = 0.35;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.roundRect(x, y, rw, rh, 4);
  ctx.fill();
  ctx.globalAlpha = 0.75;
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
}

function drawPen(
  ctx: CanvasRenderingContext2D,
  pts: AnnotationShape['points'],
  w: number,
  h: number,
  color: string,
  lw: number,
) {
  if (pts.length < 2) return;
  const d = denorm(pts, w, h);
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = lw;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(d[0].x, d[0].y);
  for (let i = 1; i < d.length; i++) {
    const mid = { x: (d[i - 1].x + d[i].x) / 2, y: (d[i - 1].y + d[i].y) / 2 };
    ctx.quadraticCurveTo(d[i - 1].x, d[i - 1].y, mid.x, mid.y);
  }
  ctx.lineTo(d[d.length - 1].x, d[d.length - 1].y);
  ctx.stroke();
  ctx.restore();
}

function redrawAll(
  canvas: HTMLCanvasElement,
  shapes: AnnotationShape[],
  inProgress?: AnnotationShape | null,
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const { width: w, height: h } = canvas;
  ctx.clearRect(0, 0, w, h);

  const all = inProgress ? [...shapes, inProgress] : shapes;
  for (const s of all) {
    if (s.type === 'arrow')     drawArrow(ctx, s.points, w, h, s.color, s.lineWidth);
    else if (s.type === 'highlight') drawHighlight(ctx, s.points, w, h, s.color);
    else                        drawPen(ctx, s.points, w, h, s.color, s.lineWidth);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
export default function SlideAnnotationCanvas({
  shapes,
  onShapesChange,
  tool,
  color,
  lineWidth,
  active,
  className = '',
}: SlideAnnotationCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing   = useRef(false);
  const current   = useRef<AnnotationShape | null>(null);

  // Resize canvas to match container
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => {
      const { offsetWidth: w, offsetHeight: h } = canvas.parentElement!;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width  = w;
        canvas.height = h;
        redrawAll(canvas, shapes, current.current);
      }
    });
    ro.observe(canvas.parentElement!);
    return () => ro.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redraw whenever shapes change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    redrawAll(canvas, shapes, current.current);
  }, [shapes]);

  const getPos = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect   = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left)  / rect.width,
      y: (e.clientY - rect.top)   / rect.height,
    };
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!active) return;
    e.preventDefault();
    drawing.current = true;
    const pos = getPos(e);
    current.current = {
      id:        `${Date.now()}-${Math.random()}`,
      type:      tool,
      color,
      lineWidth,
      points:    [pos],
    };
  }, [active, tool, color, lineWidth, getPos]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawing.current || !current.current || !active) return;
    const canvas = canvasRef.current!;
    const pos    = getPos(e);

    if (tool === 'pen') {
      current.current = { ...current.current, points: [...current.current.points, pos] };
    } else {
      // For arrow & highlight: just update end point
      current.current = {
        ...current.current,
        points: [current.current.points[0], pos],
      };
    }

    redrawAll(canvas, shapes, current.current);
  }, [active, tool, shapes, getPos]);

  const handleMouseUp = useCallback(() => {
    if (!drawing.current || !current.current) return;
    drawing.current = false;
    const shape = current.current;
    current.current = null;
    if (shape.points.length >= 2) {
      onShapesChange([...shapes, shape]);
    }
  }, [shapes, onShapesChange]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full ${active ? 'cursor-crosshair' : 'pointer-events-none'} ${className}`}
      style={{ touchAction: 'none', zIndex: 10 }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    />
  );
}

// ── Annotation Toolbar ─────────────────────────────────────────────────────
const ANNOTATION_COLORS = ['#f43f5e', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#fff', '#111'];

interface AnnotationToolbarProps {
  tool: AnnotationTool;
  onToolChange: (t: AnnotationTool) => void;
  color: string;
  onColorChange: (c: string) => void;
  lineWidth: number;
  onLineWidthChange: (w: number) => void;
  shapeCount: number;
  onUndo: () => void;
  onClear: () => void;
  onClose: () => void;
  accentColor: string;
}

export function AnnotationToolbar({
  tool, onToolChange,
  color, onColorChange,
  lineWidth, onLineWidthChange,
  shapeCount, onUndo, onClear, onClose,
  accentColor,
}: AnnotationToolbarProps) {
  const [showColors, setShowColors] = useState(false);

  const tools: Array<{ id: AnnotationTool; icon: string; label: string }> = [
    { id: 'pen',       icon: 'ri-edit-line',         label: 'Freehand pen' },
    { id: 'arrow',     icon: 'ri-arrow-right-line',  label: 'Arrow' },
    { id: 'highlight', icon: 'ri-mark-pen-line',      label: 'Highlight' },
  ];

  const widths = [2, 4, 7];

  return (
    <div
      className="flex items-center gap-1.5 px-3 py-2 rounded-2xl border border-white/15"
      style={{ backgroundColor: 'rgba(10,15,30,0.92)', backdropFilter: 'blur(12px)' }}
    >
      {/* Tool buttons */}
      {tools.map(t => (
        <button
          key={t.id}
          onClick={() => onToolChange(t.id)}
          title={t.label}
          className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-all"
          style={{
            backgroundColor: tool === t.id ? accentColor : 'rgba(255,255,255,0.08)',
            color: tool === t.id ? 'white' : 'rgba(255,255,255,0.55)',
          }}
        >
          <i className={`${t.icon} text-sm`}></i>
        </button>
      ))}

      <div className="w-px h-5 bg-white/15 mx-0.5" />

      {/* Line width */}
      {widths.map(w => (
        <button
          key={w}
          onClick={() => onLineWidthChange(w)}
          title={`Size ${w}`}
          className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-all"
          style={{ backgroundColor: lineWidth === w ? 'rgba(255,255,255,0.15)' : 'transparent' }}
        >
          <div
            className="rounded-full"
            style={{
              width: `${Math.min(w * 3, 16)}px`,
              height: `${Math.min(w * 3, 16)}px`,
              backgroundColor: color,
              opacity: lineWidth === w ? 1 : 0.4,
            }}
          />
        </button>
      ))}

      <div className="w-px h-5 bg-white/15 mx-0.5" />

      {/* Color picker */}
      <div className="relative">
        <button
          onClick={() => setShowColors(v => !v)}
          title="Color"
          className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer border-2 border-white/20"
          style={{ backgroundColor: color }}
        />
        {showColors && (
          <div
            className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 flex gap-1.5 p-2 rounded-xl border border-white/15"
            style={{ backgroundColor: 'rgba(10,15,30,0.96)', backdropFilter: 'blur(12px)' }}
          >
            {ANNOTATION_COLORS.map(c => (
              <button
                key={c}
                onClick={() => { onColorChange(c); setShowColors(false); }}
                className="w-6 h-6 rounded-full cursor-pointer transition-transform hover:scale-110 border-2"
                style={{
                  backgroundColor: c,
                  borderColor: c === color ? 'white' : 'rgba(255,255,255,0.2)',
                }}
              />
            ))}
          </div>
        )}
      </div>

      <div className="w-px h-5 bg-white/15 mx-0.5" />

      {/* Undo */}
      <button
        onClick={onUndo}
        disabled={shapeCount === 0}
        title="Undo last (Ctrl+Z)"
        className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-all text-white/50 hover:text-white hover:bg-white/10 disabled:opacity-25 disabled:pointer-events-none"
      >
        <i className="ri-arrow-go-back-line text-sm"></i>
      </button>

      {/* Clear */}
      <button
        onClick={onClear}
        disabled={shapeCount === 0}
        title="Clear all annotations"
        className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-all text-white/50 hover:text-rose-400 hover:bg-rose-500/10 disabled:opacity-25 disabled:pointer-events-none"
      >
        <i className="ri-delete-bin-line text-sm"></i>
      </button>

      <div className="w-px h-5 bg-white/15 mx-0.5" />

      {/* Count badge */}
      {shapeCount > 0 && (
        <span
          className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
          style={{ backgroundColor: `${accentColor}30`, color: accentColor }}
        >
          {shapeCount}
        </span>
      )}

      {/* Close */}
      <button
        onClick={onClose}
        title="Exit annotation mode (A)"
        className="w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-all text-white/40 hover:text-white hover:bg-white/10 ml-0.5"
      >
        <i className="ri-close-line text-sm"></i>
      </button>
    </div>
  );
}
